import { useState } from 'react';
import { ApiFailure, post } from '../lib/api';
import { isIos } from '../lib/push';

const ERROR_COPY: Record<string, string> = {
  expired: 'That link expired. Ask for a new one below.',
  used: 'That link was already used. Ask for a new one below.',
  invalid: "That link didn't work. Ask for a new one below.",
  missing: "That link didn't work. Ask for a new one below.",
  inactive: 'Your account is no longer active. Talk to a manager.',
};

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(
    ERROR_COPY[new URLSearchParams(location.search).get('error') ?? ''] ?? null,
  );

  // Called both as a form submit and from the "email me a code" button, so the
  // event is optional rather than assumed to be a FormEvent.
  async function submit(event?: React.SyntheticEvent) {
    event?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await post('/auth/request-link', { email });
      setSent(true);
    } catch (failure) {
      setError(
        failure instanceof ApiFailure ? failure.message : 'Could not reach the server. Try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function submitPassword(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await post('/auth/login', { email, password });
      location.href = '/';
    } catch (failure) {
      setError(failure instanceof ApiFailure ? failure.message : 'Could not sign in. Try again.');
      setBusy(false);
    }
  }

  async function submitCode(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await post('/auth/verify-code', { email, code });
      // Full reload rather than client-side navigation: the session cookie is
      // now set, and reloading lets SessionProvider fetch /me cleanly instead
      // of threading auth state back through this component.
      location.href = '/';
    } catch (failure) {
      setError(
        failure instanceof ApiFailure ? failure.message : 'Could not check that code. Try again.',
      );
      setBusy(false);
    }
  }

  return (
    <div className="pt-safe pb-safe flex h-full flex-col items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <img src="/icon.svg" alt="" className="mx-auto mb-4 h-16 w-16 rounded-2xl" />
          <h1 className="text-2xl font-bold text-zinc-900">The Huddle</h1>
          <p className="mt-1 text-sm text-zinc-500">Where the crew talks.</p>
        </div>

        {sent ? (
          // Deliberately the same message whether or not the address is on the
          // roster — the API does not reveal who works here, and neither does
          // this screen.
          <div className="rounded-2xl bg-brand-50 p-5">
            <p className="text-center font-medium text-brand-accent">Check your email</p>
            <p className="mt-2 text-center text-sm text-zinc-600">
              If <span className="font-medium">{email}</span> is on the crew, a code and a sign-in
              link are on the way.
            </p>

            <form onSubmit={submitCode} className="mt-4">
              <label htmlFor="code" className="mb-1.5 block text-sm font-medium text-zinc-700">
                Enter your 6-digit code
              </label>
              <input
                id="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                className="w-full rounded-xl border border-zinc-300 bg-surface px-4 py-3 text-center font-mono text-2xl tracking-[0.4em] outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-100"
              />
              <button
                type="submit"
                disabled={busy || code.length !== 6}
                className="mt-3 w-full rounded-xl bg-brand-600 py-3.5 font-semibold text-white active:bg-brand-700 disabled:opacity-50"
              >
                {busy ? 'Checking…' : 'Sign in'}
              </button>
            </form>

            {error && (
              <p role="alert" className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </p>
            )}

            {/* This is the whole reason the code exists — say so, because
                otherwise an iPhone user taps the emailed link, lands in a
                browser, and never understands why the installed app is still
                logged out. */}
            {isIos() && (
              <p className="mt-3 text-xs leading-relaxed text-zinc-500">
                On iPhone, use the code. The app on your home screen signs in separately from
                Safari, so tapping the emailed link won't sign it in.
              </p>
            )}

            <button
              type="button"
              onClick={() => {
                setSent(false);
                setCode('');
                setError(null);
              }}
              className="mt-4 w-full text-center text-sm font-medium text-brand-accent"
            >
              Use a different email
            </button>
          </div>
        ) : (
          <form onSubmit={submitPassword} className="space-y-3">
            <div>
              <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-zinc-700">
                Your work email
              </label>
              <input
                id="email"
                type="email"
                inputMode="email"
                autoComplete="username"
                autoCapitalize="off"
                autoCorrect="off"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full rounded-xl border border-zinc-300 px-4 py-3 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-100"
              />
            </div>

            <div>
              <div className="mb-1.5 flex items-baseline justify-between">
                <label htmlFor="password" className="block text-sm font-medium text-zinc-700">
                  Password
                </label>
                {/* Same action as "email me a code" below, but labelled the way
                    someone who is stuck will actually look for it. */}
                <button
                  type="button"
                  onClick={submit}
                  disabled={busy || email.length === 0}
                  className="tap-target px-2 py-2 text-xs font-medium text-brand-accent disabled:opacity-40"
                >
                  Forgot password?
                </button>
              </div>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your password"
                className="w-full rounded-xl border border-zinc-300 px-4 py-3 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-100"
              />
            </div>

            <button
              type="submit"
              disabled={busy || email.length === 0 || password.length === 0}
              className="w-full rounded-xl bg-brand-600 py-3.5 font-semibold text-white transition active:bg-brand-700 disabled:opacity-50"
            >
              {busy ? 'Signing in…' : 'Sign in'}
            </button>

            {error && (
              <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </p>
            )}

            {/* The code path stays one tap away: it is the only route in for
                anyone who has not set a password, forgot theirs, or is signing
                into a home-screen app on iOS for the first time. */}
            <div className="flex items-center gap-3 pt-1">
              <span className="h-px flex-1 bg-zinc-200" />
              <span className="text-xs text-zinc-400">or</span>
              <span className="h-px flex-1 bg-zinc-200" />
            </div>

            <button
              type="button"
              onClick={submit}
              disabled={busy || email.length === 0}
              className="w-full rounded-xl border border-zinc-300 py-3 font-medium text-zinc-700 active:bg-zinc-50 disabled:opacity-50"
            >
              Email me a code instead
            </button>

            <p className="pt-2 text-center text-xs leading-relaxed text-zinc-400">
              No password yet? Get a code, then set one under Me.
              <br />
              New here? A manager has to add you first.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
